import { useCallback, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames/bind'

import Person from 'app/common/classes/person'
import SimpleDate from 'app/common/classes/simple-date'
import PlainDateMonthSwitcher from 'app/common/ui-next/plain-date-month-picker'
import PlainDateSwitcher from 'app/common/ui-next/plain-date-switcher'
import type { Params } from 'app/common/components/Cafeteria/dataTypes/orderRequests/OrderRequest.types'
import Sticky from 'app/common/templates-next/sticky'
import { PageTemplateContent, Header2 } from 'app/common/templates-next/page-template'

import StatusFilter from '../commonComponents/StatusFilter'
import { statuses, toStatus } from '../dataTypes/orderRequests/statuses'
import { status2TranslationMap, status2ColorMap } from './statusProps'
import Content from './content/Content'

import styles from './CafeteriaOrderRequests.module.scss';

const cx = cn.bind(styles)

type Props = {
  customer: Person
  date: SimpleDate
  monthly: boolean
  editable?: boolean
  onChangeDate?: (date: string, format: string, daily: boolean) => void
  campId?: string
}

const CafeteriaOrderRequests = ({
  customer,
  date,
  monthly,
  editable,
  onChangeDate,
  campId,
}: Props) => {
  const { t } = useTranslation('cafeteria')

  const [currentStatus, setCurrentStatus] = useState<string>(statuses.All)

  const header = t('order_requests_header', {
    name: customer.kogoChego.first,
    date: date.getString(monthly ? 'MMMM YYYY' : 'DD.MM.YYYY'),
  })

  const handleChangeDate = useCallback(
    (dt: string, fm: string) => {
      if (onChangeDate) {
        onChangeDate(dt, fm, !monthly)
      }
    },
    [onChangeDate, monthly],
  )

  const params: Params = useMemo(
    () => ({
      date,
      monthly,
      customerId: customer.id,
      campId,
    }),
    [date, monthly, customer.id, campId],
  )

  return (
    <div className={styles.root}>
      <Sticky>
        {(ref, sticky) => (
          <PageTemplateContent reference={ref} className={cx({ sticky })} flex>
            <Header2 skipMargin>{header}</Header2>

            <PlainDateSwitcher
              date={date.getValue()}
              dateFormat={date.getFormat() || undefined}
              onDateChange={handleChangeDate}
              stepType={monthly ? 'month' : 'day'}
              displayDateFormat={monthly ? 'MMMM' : 'DD.MM.YYYY'}
            >
              <PlainDateMonthSwitcher
                date={date.getValue()}
                dateFormat={date.getFormat() || undefined}
                onChangeDate={onChangeDate}
                monthsAmount={24}
                monthsInFuture={1}
              />
            </PlainDateSwitcher>
          </PageTemplateContent>
        )}
      </Sticky>
      <Sticky>
        {(ref) => (
          <PageTemplateContent reference={ref} flex>
            <StatusFilter
              currentStatus={currentStatus}
              onClickItem={setCurrentStatus}
              status2ColorMap={status2ColorMap}
              status2TranslationMap={status2TranslationMap}
              title={t('order_requests_filter_title', 'Показать')}
              onTranslate={t}
            />
          </PageTemplateContent>
        )}
      </Sticky>

      <Content
        status={toStatus(currentStatus)}
        params={params}
        editable={editable}
        customer={customer}
      />
    </div>
  )
}

export default CafeteriaOrderRequests
